import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HasAccess, useHasAccess } from "@/components/common/has-access";
import { useUpdateFormMapping } from "../hooks/use-form-mapping";
import { FormFieldMapping, FormQuestion } from "@/pages/forms/types/form";

// Radix Select forbids value="" — sentinel for "not mapped".
const NONE = "NONE";

// A form that can't resolve these can never create a lead.
const REQUIRED_FIELDS = ["full_name", "email", "phone_number"];

export default function FormFieldMappingCard({
  formId,
  questions,
  mapping,
}: {
  formId: string;
  questions: FormQuestion[];
  mapping: FormFieldMapping[];
}) {
  const [q, setQ] = useState("");
  const update = useUpdateFormMapping(formId);

  // View-only on the Forms area: the mapping stays readable, the dropdowns do
  // not open. The Update button is already behind <HasAccess>; without this the
  // selects would still change to a draft that could never be saved.
  const canWrite = useHasAccess("write");

  // The 12 semantic fields come from the server-built mapping rows —
  // no duplicated client-side list.
  const semanticFields = useMemo(() => mapping.map((m) => m.field), [mapping]);

  // Saved state, inverted: question_id -> semantic field (DB or legacy).
  const savedByQuestion = useMemo(() => {
    const map: Record<string, string> = {};
    for (const m of mapping) if (m.question_id) map[m.question_id] = m.field;
    return map;
  }, [mapping]);

  // Draft selections overlay the saved state; NONE means explicitly unmapped.
  const [draft, setDraft] = useState<Record<string, string>>({});
  const selectionFor = (questionId: string) =>
    draft[questionId] ?? savedByQuestion[questionId] ?? NONE;

  const selections = useMemo(
    () =>
      questions
        .map((qn) => ({ questionId: qn.id, field: selectionFor(qn.id) }))
        .filter((s) => s.field !== NONE),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questions, draft, savedByQuestion],
  );

  // Two questions on the same semantic field would silently collapse into one
  // key in the payload object — block the save instead.
  const duplicateFields = useMemo(() => {
    const seen = new Set<string>();
    const dups = new Set<string>();
    for (const s of selections) {
      if (seen.has(s.field)) dups.add(s.field);
      seen.add(s.field);
    }
    return [...dups];
  }, [selections]);

  const missingRequired = REQUIRED_FIELDS.filter(
    (f) => !selections.some((s) => s.field === f),
  );

  const dirty = useMemo(() => {
    const current: Record<string, string> = {};
    for (const s of selections) current[s.questionId] = s.field;
    const savedKeys = Object.keys(savedByQuestion);
    const currentKeys = Object.keys(current);
    if (savedKeys.length !== currentKeys.length) return true;
    return currentKeys.some((k) => savedByQuestion[k] !== current[k]);
  }, [savedByQuestion, selections]);

  const onSave = () => {
    // Whole-map replace: only selected rows are sent; NONE rows are omitted
    // and the server unmaps them.
    const field_mapping = Object.fromEntries(
      selections.map((s) => [s.field, s.questionId]),
    );
    update.mutate({ field_mapping }, { onSuccess: () => setDraft({}) });
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return questions;
    return questions.filter(
      (qn) =>
        (qn.title ?? "").toLowerCase().includes(s) ||
        qn.id.toLowerCase().includes(s) ||
        selectionFor(qn.id).toLowerCase().includes(s),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, q, draft, savedByQuestion]);

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">Field mapping</CardTitle>
          <HasAccess permission="write">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={update.isPending || duplicateFields.length > 0 || !dirty}
              onClick={onSave}
            >
              {update.isPending ? "Saving…" : "Update mapping"}
            </Button>
          </HasAccess>
        </div>
        <p className="text-xs text-gray-400">
          Map each question to the semantic field used during user/lead
          creation. Unselected questions are left unmapped.
        </p>
        {duplicateFields.length > 0 && (
          <p className="text-xs text-red-600">
            Each semantic field can be mapped to only one question:{" "}
            {duplicateFields.join(", ")}
          </p>
        )}
        {duplicateFields.length === 0 && missingRequired.length > 0 && (
          <p className="text-xs text-amber-600">
            No leads can be created from this form until these are mapped:{" "}
            {missingRequired.join(", ")}
          </p>
        )}
        <Input
          placeholder="Search questions…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-8"
        />
      </CardHeader>
      <CardContent>
        <div className="h-[360px] overflow-y-auto rounded-md border border-gray-100">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50">
              <tr className="text-left text-xs text-gray-500">
                <th className="py-1.5 px-3">Question</th>
                <th className="py-1.5 px-3 w-24">Type</th>
                <th className="py-1.5 px-3 w-48">Semantic field</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((qn) => (
                <tr key={qn.id} className="border-t border-gray-100">
                  <td className="py-1.5 px-3">
                    <p className="line-clamp-2">{qn.title ?? "—"}</p>
                    <p className="font-mono text-[11px] text-gray-400">
                      {qn.id}
                    </p>
                  </td>
                  <td className="py-1.5 px-3 text-xs text-gray-500">
                    {qn.type ?? "—"}
                  </td>
                  <td className="py-1.5 px-3">
                    <Select
                      disabled={!canWrite}
                      value={selectionFor(qn.id)}
                      onValueChange={(value) =>
                        setDraft((d) => ({ ...d, [qn.id]: value }))
                      }
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>— not mapped —</SelectItem>
                        {semanticFields.map((f) => (
                          <SelectItem key={f} value={f}>
                            {f}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={3}
                    className="py-4 px-3 text-center text-sm text-gray-400"
                  >
                    {questions.length === 0
                      ? "No questions synced for this form"
                      : "No matches"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
